import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api, resolveAsset } from '../../api/client';
import { useT } from '../../i18n';

// Manual "build a story" form. Captures the order/customer details + title +
// initial scenes (text and optional per-scene font size), then creates a manual
// Story and a linked admin_manual Order and drops the admin into the full story
// editor (/admin/stories/:id) to add the cover, scene images, and back cover.
//
// The "تعبئة تلقائية" (AI fill) panel lets the admin paste ONE raw blob (story
// text + child details + customer contact + price/language/title) and attach
// images; the server analyzes it (see routes/admin.js stories/build/analyze)
// and the response prefills every field below — including per-scene images,
// the dedication portrait, and a ready cover — for review before creating.

function emptyScene() {
  return { text: '', fontSize: '', imageUrl: null };
}

const AI_IMAGE_ROLES = {
  portrait: 'صورة الطفل (الإهداء)',
  cover: 'الغلاف',
  scene: 'مشهد',
  other: 'غير مصنّفة',
};

export default function AdminStoryBuild() {
  const t = useT();
  const navigate = useNavigate();

  const PRODUCT_TYPES = useMemo(() => [
    { value: 'softcover', label: t.adminProductSoftcover },
    { value: 'hardcover', label: t.adminProductHardcover },
    { value: 'digital', label: t.adminProductDigital },
  ], [t]);

  const [title, setTitle] = useState('');
  // Hero (the child the book is about) — distinct from the customer contact.
  // name + gender drive the gendered back-cover dedication/summary + the
  // displayed hero name; age is informational.
  const [hero, setHero] = useState({ name: '', gender: '', age: '' });
  // Optional dedication-page portrait (the child's photo in the medallion).
  // Uploaded after the story is created (the upload route needs its id).
  const [portrait, setPortrait] = useState(null);
  const [portraitPreview, setPortraitPreview] = useState(null);
  useEffect(() => {
    if (!portrait) { setPortraitPreview(null); return; }
    const url = URL.createObjectURL(portrait);
    setPortraitPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [portrait]);
  const [contact, setContact] = useState({ name: '', email: '', phone: '' });
  const [productType, setProductType] = useState('softcover');
  const [price, setPrice] = useState(''); // shekels, converted to cents on submit
  const [qty, setQty] = useState(1);
  const [status, setStatus] = useState('pending_payment');
  // 'preview'  → behaves like a customer pre-purchase preview: illustrate only
  //              the cover + first scene; the rest render locked. Send the
  //              customer the /preview link to view and pay (Stripe self-checkout).
  // 'full'     → a finished, production-ready book (all scenes illustrated).
  const [buildMode, setBuildMode] = useState('full');
  // Story-text language: 'ar' (default, Arabic/Hebrew RTL) or 'en' (English LTR).
  // Drives the input direction here and the scene-text direction in the viewer +
  // print PDF (persisted on personalization.textLanguage).
  const [textLanguage, setTextLanguage] = useState('ar');
  const inputDir = textLanguage === 'en' ? 'ltr' : 'rtl';
  const [scenes, setScenes] = useState([emptyScene()]);
  // Optional font size (pt) applied to every scene as a default. A per-scene
  // fontSize, when set, still overrides this global value.
  const [allFontSize, setAllFontSize] = useState('');

  // Pre-uploaded image URLs from the AI-assist analyze step. `portraitUrl`
  // complements the file-based `portrait` above (URL wins when no file is
  // chosen); `coverUrl` is a ready cover front panel passed through to create.
  const [portraitUrl, setPortraitUrl] = useState(null);
  const [coverUrl, setCoverUrl] = useState(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // ── AI fill panel state ──
  const [aiOpen, setAiOpen] = useState(false);
  const [aiText, setAiText] = useState('');
  const [aiFiles, setAiFiles] = useState([]);           // File[]
  const [aiPreviews, setAiPreviews] = useState([]);     // object URLs, same order
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiImages, setAiImages] = useState(null);       // classified images from the server
  useEffect(() => {
    const urls = aiFiles.map((f) => URL.createObjectURL(f));
    setAiPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [aiFiles]);

  // Apply the analyze response onto the form. Only overwrite a field when the
  // model actually extracted something, so a partial blob never wipes values
  // the admin already typed.
  const applyAnalysis = (r) => {
    if (r.title) setTitle(r.title);
    if (r.textLanguage) setTextLanguage(r.textLanguage);
    setHero((prev) => ({
      name: r.hero?.name || prev.name,
      gender: r.hero?.gender || prev.gender,
      age: r.hero?.age || prev.age,
    }));
    setContact((prev) => ({
      name: r.contact?.name || prev.name,
      email: r.contact?.email || prev.email,
      phone: r.contact?.phone || prev.phone,
    }));
    if (r.productType) setProductType(r.productType);
    if (r.priceShekels != null) setPrice(String(r.priceShekels));
    if (r.qty != null) setQty(r.qty);
    if (r.paid === true) setStatus('paid');
    else if (r.paid === false) setStatus('pending_payment');
    if (r.buildMode) setBuildMode(r.buildMode);

    const images = Array.isArray(r.images) ? r.images : [];
    if (Array.isArray(r.scenes) && r.scenes.length) {
      const next = r.scenes.map((s) => ({ text: s.text || '', fontSize: '', imageUrl: null }));
      for (const im of images) {
        if (im.role === 'scene' && im.sceneIndex != null && next[im.sceneIndex]) {
          next[im.sceneIndex].imageUrl = im.url;
        }
      }
      setScenes(next);
    }
    const portraitIm = images.find((im) => im.role === 'portrait');
    if (portraitIm) { setPortraitUrl(portraitIm.url); setPortrait(null); }
    const coverIm = images.find((im) => im.role === 'cover');
    if (coverIm) setCoverUrl(coverIm.url);
    setAiImages(images);
  };

  const analyze = async () => {
    setAiError('');
    if (!aiText.trim()) { setAiError('الصِق نصّ الطلب أولًا'); return; }
    setAiBusy(true);
    try {
      const fd = new FormData();
      fd.append('text', aiText);
      aiFiles.forEach((f) => fd.append('images', f));
      const r = await api.postForm('/api/admin/stories/build/analyze', fd);
      applyAnalysis(r);
    } catch (e) {
      setAiError(e.message || 'فشل التحليل — حاول مجددًا');
    } finally {
      setAiBusy(false);
    }
  };

  const setScene = (i, patch) =>
    setScenes((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const addScene = () => setScenes((prev) => [...prev, emptyScene()]);
  const removeScene = (i) => setScenes((prev) => prev.filter((_, idx) => idx !== i));
  const moveScene = (i, dir) =>
    setScenes((prev) => {
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = prev.slice();
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  const submit = async () => {
    setError('');
    if (!title.trim()) { setError(t.adminEnterStoryTitle); return; }
    setSaving(true);
    try {
      const res = await api.post('/api/admin/stories/build', {
        title: title.trim(),
        personalization: {
          name: hero.name.trim(),
          gender: hero.gender,
          age: hero.age.trim(),
        },
        contact,
        productType,
        priceCents: Math.round((Number(price) || 0) * 100),
        qty: Number(qty) || 1,
        status,
        buildMode,
        textLanguage,
        // Pre-uploaded URLs from the AI-assist flow (null when unused).
        portraitUrl: portrait ? null : portraitUrl,
        coverImageUrl: coverUrl,
        scenes: scenes.map((s) => ({
          text: s.text,
          imageUrl: s.imageUrl || null,
          // Per-scene size wins; otherwise fall back to the "all scenes" value;
          // otherwise null (automatic).
          fontSize: s.fontSize !== '' ? Number(s.fontSize)
            : allFontSize !== '' ? Number(allFontSize)
            : null,
        })),
      });
      // Upload the dedication portrait now that the story exists (the route
      // needs its id). Best-effort: a failure here shouldn't lose the story —
      // the admin can still add it from the editor.
      if (portrait) {
        try {
          const fd = new FormData();
          fd.append('image', portrait);
          await api.postForm(`/api/admin/stories/${res.storyId}/portrait/upload`, fd);
        } catch (e) {
          console.error('[build] portrait upload failed:', e);
        }
      }
      // Hand off to the full editor to add the cover + scene images.
      navigate(`/admin/stories/${res.storyId}`);
    } catch (e) {
      setError(e.message || t.adminCreateStoryFailed);
      setSaving(false);
    }
  };

  return (
    <div className="admin-page">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <h1 className="page-title">{t.adminBuildStoryTitle}</h1>
        <Link to="/admin/orders" className="ghost-btn" style={{ padding: '8px 18px' }}>{t.adminBackToOrders}</Link>
      </div>
      <p className="admin-scene-subtitle">
        {t.adminBuildStoryDesc}
      </p>

      {/* AI fill: paste one raw blob + attach images → the server analyzes and
          prefills the whole form; the admin reviews then presses create. */}
      <div className="admin-scene-card" style={{ display: 'block', border: '2px solid #6366f1' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          <h2 className="page-title" style={{ fontSize: '1.05rem', margin: 0 }}>✨ تعبئة تلقائية بالذكاء الاصطناعي</h2>
          <button type="button" className="ghost-btn" onClick={() => setAiOpen((v) => !v)}>
            {aiOpen ? 'إخفاء' : 'فتح'}
          </button>
        </div>
        {aiOpen && (
          <div style={{ marginTop: 12 }}>
            <p className="admin-scene-meta" style={{ marginTop: 0 }}>
              الصِق هنا كل تفاصيل الطلب دفعة واحدة: نص القصة كاملًا، اسم الطفل وعمره وجنسه،
              اسم الزبون وبريده وهاتفه، السعر، نوع المنتج، اللغة، العنوان… وأرفق الصور
              (صورة الطفل، رسومات المشاهد، الغلاف). سيحلَّل كل شيء ويعبَّأ النموذج تلقائيًا —
              راجِع الحقول ثم اضغط «إنشاء القصة».
            </p>
            <textarea
              className="admin-scene-textarea" rows={10} dir="auto" value={aiText}
              onChange={(e) => setAiText(e.target.value)}
              placeholder={'مثال:\nقصة لطفل اسمه كامل عمره ٧ سنين…\nالعنوان: …\nإيميل الزبون: …\n(ثم نص القصة كاملًا بمشاهده)'}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
              <label className="ghost-btn" style={{ display: 'inline-block', cursor: 'pointer' }}>
                <input type="file" accept="image/*" multiple style={{ display: 'none' }}
                  onChange={(e) => setAiFiles(Array.from(e.target.files || []))} />
                {aiFiles.length ? `تغيير الصور (${aiFiles.length})` : 'إرفاق صور'}
              </label>
              {aiFiles.length > 0 && (
                <button type="button" className="admin-link-btn admin-link-danger" onClick={() => setAiFiles([])}>
                  إزالة الصور
                </button>
              )}
              <button type="button" className="submit-btn" style={{ marginInlineStart: 'auto' }}
                onClick={analyze} disabled={aiBusy}>
                {aiBusy ? 'جارٍ التحليل…' : 'حلّل واملأ النموذج'}
              </button>
            </div>
            {aiPreviews.length > 0 && (
              <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
                {aiPreviews.map((u, i) => {
                  // After analysis, label each thumbnail with its detected role.
                  const im = aiImages?.[i];
                  const label = im
                    ? (im.role === 'scene' && im.sceneIndex != null
                        ? `${AI_IMAGE_ROLES.scene} ${im.sceneIndex + 1}`
                        : AI_IMAGE_ROLES[im.role] || AI_IMAGE_ROLES.other)
                    : null;
                  return (
                    <div key={i} style={{ textAlign: 'center' }}>
                      <img src={u} alt="" style={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 10, border: '2px solid #e5e7eb' }} />
                      {label && <div style={{ fontSize: '0.75rem', color: '#6366f1', marginTop: 4, fontWeight: 600 }}>{label}</div>}
                    </div>
                  );
                })}
              </div>
            )}
            {aiError && <p className="auth-error" style={{ marginTop: 10 }}>{aiError}</p>}
            {aiImages && !aiError && (
              <p className="admin-scene-meta" style={{ marginTop: 10, color: '#16a34a' }}>
                ✓ تمت التعبئة — راجِع الحقول أدناه ثم اضغط «إنشاء القصة ومتابعة التحرير».
              </p>
            )}
          </div>
        )}
      </div>

      {/* Version: preview vs production-ready. Drives whether the book is treated
          as a customer pre-purchase preview (cover + scene 1, rest locked, send
          the /preview link to view + pay) or a finished full book. */}
      <div className="admin-scene-card" style={{ display: 'block' }}>
        <h2 className="page-title" style={{ fontSize: '1.05rem', marginTop: 0 }}>{t.adminVersionType}</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
          <label
            style={{
              display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer',
              border: `2px solid ${buildMode === 'full' ? '#6366f1' : '#e5e7eb'}`,
              borderRadius: 12, padding: '12px 14px',
            }}
          >
            <input type="radio" name="buildMode" value="full" checked={buildMode === 'full'}
              onChange={() => setBuildMode('full')} style={{ marginTop: 4 }} />
            <span>
              <strong>{t.adminFullVersion}</strong>
              <span style={{ display: 'block', fontSize: '0.85rem', color: '#6b7280', marginTop: 4 }}>
                {t.adminFullVersionDesc}
              </span>
            </span>
          </label>
          <label
            style={{
              display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer',
              border: `2px solid ${buildMode === 'preview' ? '#6366f1' : '#e5e7eb'}`,
              borderRadius: 12, padding: '12px 14px',
            }}
          >
            <input type="radio" name="buildMode" value="preview" checked={buildMode === 'preview'}
              onChange={() => setBuildMode('preview')} style={{ marginTop: 4 }} />
            <span>
              <strong>{t.adminPreviewVersion}</strong>
              <span style={{ display: 'block', fontSize: '0.85rem', color: '#6b7280', marginTop: 4 }}>
                {t.adminPreviewVersionDesc}
              </span>
            </span>
          </label>
        </div>
      </div>

      {/* Order / customer details */}
      <div className="admin-scene-card" style={{ display: 'block' }}>
        <h2 className="page-title" style={{ fontSize: '1.05rem', marginTop: 0 }}>{t.adminOrderDetailsHeading}</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          <div>
            <label className="admin-field-label">{t.adminCustomerName}</label>
            <input className="admin-title-input" type="text" dir="rtl" value={contact.name}
              onChange={(e) => setContact({ ...contact, name: e.target.value })} />
          </div>
          <div>
            <label className="admin-field-label">{t.adminEmailLabel}</label>
            <input className="admin-title-input" type="email" dir="ltr" value={contact.email}
              onChange={(e) => setContact({ ...contact, email: e.target.value })} />
          </div>
          <div>
            <label className="admin-field-label">{t.adminPhoneLabel}</label>
            <input className="admin-title-input" type="tel" dir="ltr" value={contact.phone}
              onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
          </div>
          <div>
            <label className="admin-field-label">{t.adminProductTypeField}</label>
            <select className="admin-title-input" value={productType} onChange={(e) => setProductType(e.target.value)}>
              {PRODUCT_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label className="admin-field-label">{t.adminPriceField}</label>
            <input className="admin-title-input" type="number" min="0" step="1" dir="ltr" value={price}
              onChange={(e) => setPrice(e.target.value)} placeholder="0" />
          </div>
          <div>
            <label className="admin-field-label">{t.adminQuantity}</label>
            <input className="admin-title-input" type="number" min="1" step="1" dir="ltr" value={qty}
              onChange={(e) => setQty(e.target.value)} />
          </div>
          <div>
            <label className="admin-field-label">{t.adminPaymentStatus}</label>
            <select className="admin-title-input" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="pending_payment">{t.adminUnpaid}</option>
              <option value="paid">{t.adminPaid}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Title + story-text language */}
      <div className="admin-scene-card" style={{ display: 'block' }}>
        <div style={{ marginBottom: 14 }}>
          <label className="admin-field-label">لغة نص القصة</label>
          <select className="admin-title-input" style={{ maxWidth: 240 }} value={textLanguage}
            onChange={(e) => setTextLanguage(e.target.value)}>
            <option value="ar">عربي (Arabic / Hebrew — RTL)</option>
            <option value="en">إنجليزي (English — LTR)</option>
          </select>
          <p className="admin-scene-meta" style={{ marginTop: 6 }}>
            اختَر «إنجليزي» لكتابة نص القصة بالإنجليزية؛ سيظهر النص من اليسار إلى اليمين في المعاينة وملف الطباعة.
          </p>
        </div>
        <label className="admin-field-label">عنوان القصة</label>
        <input className="admin-title-input" type="text" dir={inputDir} value={title}
          onChange={(e) => setTitle(e.target.value)} placeholder="عنوان القصة كما يظهر على الغلاف والكتاب" />

        {/* Ready cover passed in via the AI-assist flow. */}
        {coverUrl && (
          <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <img src={resolveAsset(coverUrl)} alt=""
              style={{ width: 84, height: 88, borderRadius: 10, objectFit: 'cover', border: '2px solid #eee' }} />
            <div>
              <label className="admin-field-label">غلاف جاهز (من التعبئة التلقائية)</label>
              <button type="button" className="admin-link-btn admin-link-danger" onClick={() => setCoverUrl(null)}>
                إزالة الغلاف
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Hero (the child the book is about) — feeds the gendered back-cover text */}
      <div className="admin-scene-card" style={{ display: 'block' }}>
        <h2 className="page-title" style={{ fontSize: '1.05rem', marginTop: 0 }}>تفاصيل البطل</h2>
        <p className="admin-scene-meta">اسم البطل وجنسه يظهران في الإهداء والنص التعريفي على الغلاف الخلفي.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          <div>
            <label className="admin-field-label">اسم البطل</label>
            <input className="admin-title-input" type="text" dir="rtl" value={hero.name}
              onChange={(e) => setHero({ ...hero, name: e.target.value })} placeholder="اسم الطفل في القصة" />
          </div>
          <div>
            <label className="admin-field-label">الجنس</label>
            <select className="admin-title-input" value={hero.gender}
              onChange={(e) => setHero({ ...hero, gender: e.target.value })}>
              <option value="">— غير محدّد —</option>
              <option value="ولد">ولد</option>
              <option value="بنت">بنت</option>
            </select>
          </div>
          <div>
            <label className="admin-field-label">العمر</label>
            <input className="admin-title-input" type="text" dir="ltr" value={hero.age}
              onChange={(e) => setHero({ ...hero, age: e.target.value })} placeholder="مثال: 4" />
          </div>
        </div>

        {/* Dedication-page portrait: the child's photo shown in the medallion on
            the dedication spread. Optional; framing is adjusted later in the editor. */}
        <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          {(portraitPreview || portraitUrl) && (
            <img src={portraitPreview || resolveAsset(portraitUrl)} alt=""
              style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', border: '3px solid #eee' }} />
          )}
          <div>
            <label className="admin-field-label">صورة صفحة الإهداء (اختياري)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <label className="ghost-btn" style={{ display: 'inline-block', cursor: 'pointer' }}>
                <input type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={(e) => setPortrait(e.target.files?.[0] || null)} />
                {(portrait || portraitUrl) ? 'تغيير الصورة' : 'رفع صورة من جهازك'}
              </label>
              {(portrait || portraitUrl) && (
                <button type="button" className="admin-link-btn admin-link-danger"
                  onClick={() => { setPortrait(null); setPortraitUrl(null); }}>إزالة</button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Scenes */}
      <div className="admin-scene-card" style={{ display: 'block' }}>
        <h2 className="page-title" style={{ fontSize: '1.05rem', marginTop: 0 }}>المشاهد</h2>
        <div style={{ marginBottom: 14, paddingBottom: 14, borderBottom: '1px solid #eee' }}>
          <label className="admin-field-label">حجم الخط لكل المشاهد (نقطة pt — يُطبَّق على جميع المشاهد)</label>
          <input className="admin-title-input" type="number" min="6" max="48" step="0.5" dir="ltr"
            style={{ maxWidth: 160 }} value={allFontSize}
            onChange={(e) => setAllFontSize(e.target.value)} placeholder="تلقائي" />
          <p className="admin-scene-meta" style={{ marginTop: 6 }}>
            يمكنك تغيير حجم الخط لمشهد معيّن أدناه، وسيتجاوز القيمة العامة.
          </p>
        </div>
        {scenes.map((s, i) => (
          <div key={i} style={{ borderTop: i ? '1px solid #eee' : 'none', paddingTop: i ? 14 : 0, marginTop: i ? 14 : 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <label className="admin-field-label">نص المشهد {i + 1}</label>
              <div style={{ display: 'inline-flex', gap: 6 }}>
                <button type="button" className="ghost-btn" onClick={() => moveScene(i, -1)} disabled={i === 0} title="تحريك لأعلى">↑</button>
                <button type="button" className="ghost-btn" onClick={() => moveScene(i, 1)} disabled={i === scenes.length - 1} title="تحريك لأسفل">↓</button>
                <button type="button" className="admin-link-btn admin-link-danger" onClick={() => removeScene(i)} disabled={scenes.length === 1}>حذف</button>
              </div>
            </div>
            {s.imageUrl && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <img src={resolveAsset(s.imageUrl)} alt=""
                  style={{ width: 72, height: 72, borderRadius: 10, objectFit: 'cover', border: '2px solid #e5e7eb' }} />
                <button type="button" className="admin-link-btn admin-link-danger"
                  onClick={() => setScene(i, { imageUrl: null })}>إزالة صورة المشهد</button>
              </div>
            )}
            <textarea className="admin-scene-textarea" rows={4} dir={inputDir} value={s.text}
              onChange={(e) => setScene(i, { text: e.target.value })} />
            <label className="admin-field-label">حجم الخط (نقطة pt — اتركه فارغًا لاستخدام القيمة العامة)</label>
            <input className="admin-title-input" type="number" min="6" max="48" step="0.5" dir="ltr"
              style={{ maxWidth: 160 }} value={s.fontSize}
              onChange={(e) => setScene(i, { fontSize: e.target.value })}
              placeholder={allFontSize !== '' ? `${allFontSize} (عام)` : 'تلقائي'} />
          </div>
        ))}
        <div style={{ marginTop: 14 }}>
          <button type="button" className="ghost-btn" onClick={addScene}>+ إضافة مشهد</button>
        </div>
      </div>

      {error && <p className="auth-error">{error}</p>}

      <div className="admin-scene-actions" style={{ marginTop: 8 }}>
        <button type="button" className="submit-btn" onClick={submit} disabled={saving}>
          {saving ? 'جارٍ الإنشاء…' : 'إنشاء القصة ومتابعة التحرير'}
        </button>
      </div>
    </div>
  );
}
